/**
 * Optional engine: a local text-to-speech server (for example Piper).
 *
 * Nothing leaves your computer — the server runs on localhost. It is switched
 * off by default; turn it on in Settings after starting a server.
 *
 * Expected server contract (see README → "Adding a local Arabic TTS engine"):
 *
 *   GET  <baseUrl>/voices
 *        → [ { "id": "ar_JO-kareem-medium", "name": "Kareem", "lang": "ar-JO" }, … ]
 *
 *   POST <baseUrl>/tts     Content-Type: application/json
 *        { "text": "...", "lang": "ar", "voice": "<id|null>", "rate": 0.8 }
 *        → audio/wav (or any audio type the browser can play)
 *
 * A tiny Python wrapper that provides exactly this is included in the README.
 */
import type { SpeakOptions, SpeechEngine, TtsVoice } from './types';

export class PiperEngine implements SpeechEngine {
  readonly id = 'piper' as const;
  readonly displayNameEn = 'Local voice server (Piper)';
  readonly displayNameAr = 'خادم صوت محلي (Piper)';
  readonly supportsPause = true;

  private voices: TtsVoice[] = [];
  private listeners = new Set<() => void>();
  private audio: HTMLAudioElement | null = null;
  private baseUrl: string;
  lastError: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
  }

  setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
  }

  async init(): Promise<void> {
    try {
      const res = await fetch(`${this.baseUrl}/voices`, { method: 'GET' });
      if (!res.ok) throw new Error(`Server answered ${res.status}`);
      const data = (await res.json()) as Array<{ id?: string; name?: string; lang?: string }>;
      this.voices = (Array.isArray(data) ? data : [])
        .filter((v) => typeof v.id === 'string')
        .map((v) => {
          const lang = typeof v.lang === 'string' ? v.lang : 'ar';
          return {
            id: v.id as string,
            name: typeof v.name === 'string' ? v.name : (v.id as string),
            lang,
            isArabic: /^ar/i.test(lang),
            isEnglish: /^en/i.test(lang),
            isDefault: false,
          };
        });
      this.lastError = null;
    } catch (err) {
      this.voices = [];
      this.lastError = err instanceof Error ? err.message : 'Local voice server not reachable';
    }
    this.listeners.forEach((l) => l());
  }

  getVoices(): TtsVoice[] {
    return this.voices;
  }

  onVoicesChanged(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async speak(text: string, options: SpeakOptions): Promise<void> {
    if (options.signal.aborted) return;
    let objectUrl: string | null = null;
    try {
      const res = await fetch(`${this.baseUrl}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          lang: options.lang,
          voice: options.voiceId ?? null,
          rate: options.rate,
        }),
        signal: options.signal,
      });
      if (!res.ok) throw new Error(`Server answered ${res.status}`);
      const blob = await res.blob();
      if (options.signal.aborted) return;

      objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      this.audio = audio;
      this.lastError = null;

      await new Promise<void>((resolve) => {
        const done = () => {
          options.signal.removeEventListener('abort', onAbort);
          resolve();
        };
        const onAbort = () => {
          audio.pause();
          done();
        };
        audio.onended = done;
        audio.onerror = done;
        options.signal.addEventListener('abort', onAbort);
        void audio.play().catch(done);
      });
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        this.lastError = err instanceof Error ? err.message : 'Local voice server not reachable';
        this.listeners.forEach((l) => l());
      }
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      this.audio = null;
    }
  }

  pause(): void {
    this.audio?.pause();
  }

  resume(): void {
    void this.audio?.play().catch(() => undefined);
  }

  cancel(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
  }
}
